import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Plus, Trash2, Settings, AlertTriangle, Check, X, FileText, RefreshCw, Archive, Edit, Building2, Download, MessageSquare, BookOpen, Eye, Scale, Landmark } from 'lucide-react';
import apiService from '../services/api';
import AgentChannels from './AgentChannels';
import AgentPlaybook from './AgentPlaybook';
import ClassesProcessuaisTab from '../components/settings/ClassesProcessuaisTab';
import TribunaisTab from '../components/settings/TribunaisTab';
import TribunalPicker from '../components/settings/TribunalPicker';
import { useAuth } from '../context/AuthContext';

export default function ConfiguracoesPage() {
  const { user } = useAuth();
  // Perfis somente leitura (ex.: TESTER) visualizam, mas não alteram dados.
  const isReadOnly = user?.role === 'TESTER';

  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'classificacoes');

  useEffect(() => {
    const t = searchParams.get('tab');
    if (t && t !== activeTab) setActiveTab(t);
  }, [searchParams]);

  function handleTabChange(t) {
    setActiveTab(t);
    setSearchParams({ tab: t });
  }
  // Estados para o formulário
  const [termo, setTermo] = useState('');
  const [prioridade, setPrioridade] = useState('NORMAL');
  const [arquivamentoAutomatico, setArquivamentoAutomatico] = useState(false);
  const [reasoning, setReasoning] = useState('');
  const [tiposJustica, setTiposJustica] = useState({
    comum: false,
    trabalho: false,
    federal: false
  });
  const [tribunalSiglas, setTribunalSiglas] = useState([]);
  const [tribunals, setTribunals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Estados para a lista
  const [classificacoes, setClassificacoes] = useState([]);
  const [loadingList, setLoadingList] = useState(true);

  // Estados para edição
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({
    name: '',
    priority: 'NORMAL',
    justiceTypes: { comum: false, trabalho: false, federal: false },
    tribunalSiglas: [],
    actions: { archive: false },
    reasoning: ''
  });
  
  // Estado para controlar o collapse do formulário
  const [showForm, setShowForm] = useState(false);

  // Estados para empresas
  const [empresas, setEmpresas] = useState([]);
  const [loadingEmpresas, setLoadingEmpresas] = useState(false);
  const [showEmpresaForm, setShowEmpresaForm] = useState(false);
  const [empresaForm, setEmpresaForm] = useState({
    nome: '',
    tier: 'TIER I',
    tiposJustica: {
      comum: false,
      trabalho: false,
      federal: false
    }
  });
  const [editingEmpresaId, setEditingEmpresaId] = useState(null);
  const [editEmpresaForm, setEditEmpresaForm] = useState({
    nome: '',
    tier: 'TIER I',
    justiceTypes: { comum: false, trabalho: false, federal: false }
  });

  // Opções de prioridade (conforme API 2ADV: Baixa → Normal → Alta → Urgente)
  const prioridades = [
    { value: 'BAIXA', label: 'Baixa', color: 'bg-gray-100 text-gray-700' },
    { value: 'NORMAL', label: 'Normal', color: 'bg-blue-100 text-blue-700' },
    { value: 'ALTA', label: 'Alta', color: 'bg-yellow-100 text-yellow-700' },
    { value: 'URGENTE', label: 'Urgente', color: 'bg-red-100 text-red-700' }
  ];

  // Opções de tier para empresas
  const tiers = [
    { value: 'TIER I', label: 'TIER I', color: 'bg-green-100 text-green-700' },
    { value: 'TIER II', label: 'TIER II', color: 'bg-yellow-100 text-yellow-700' },
    { value: 'TIER III', label: 'TIER III', color: 'bg-red-100 text-red-700' }
  ];

  useEffect(() => {
    if (activeTab === 'classificacoes') {
      fetchClassificacoes();
    } else if (activeTab === 'empresas') {
      fetchEmpresas();
    }
  }, [activeTab]);

  useEffect(() => {
    fetchTribunals();
  }, []);

  // Automatically set archive action when priority is "BAIXA"
  useEffect(() => {
    if (prioridade === 'BAIXA') {
      setArquivamentoAutomatico(true);
    } else {
      setArquivamentoAutomatico(false);
    }
  }, [prioridade]);

  // Buscar classificações do backend
  const fetchClassificacoes = async () => {
    try {
      setLoadingList(true);
      const data = await apiService.get('/settings/classifications');
      setClassificacoes(data || []);
    } catch (error) {
      // Error logged by API service
      setClassificacoes([]);
    } finally {
      setLoadingList(false);
    }
  };

  const fetchTribunals = async () => {
    try {
      const data = await apiService.get('/settings/tribunals');
      setTribunals(data || []);
    } catch (error) {
      setTribunals([]);
    }
  };

  // Função para lidar com mudanças nos checkboxes de tipos de justiça
  const handleTipoJusticaChange = (tipo, checked) => {
    setTiposJustica(prev => {
      const next = { ...prev, [tipo]: checked };
      const selectedTypes = Object.keys(next).filter((key) => next[key]);
      setTribunalSiglas((current) =>
        current.filter((sigla) => {
          const tribunal = tribunals.find((item) => item.sigla === sigla);
          return !selectedTypes.length || (tribunal && selectedTypes.includes(tribunal.justiceType));
        }),
      );
      return next;
    });
  };

  // Cadastrar nova classificação
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!termo.trim()) {
      setError('Termo para pesquisar é obrigatório');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Criar payload base
      const payload = {
        name: termo.trim(),
        priority: prioridade
      };

      // Se o checkbox de arquivamento estiver marcado, adicionar ação
      if (arquivamentoAutomatico) {
        payload.actions = ['archive'];
      }

      // Adicionar tipos de justiça selecionados
      const tiposSelecionados = Object.keys(tiposJustica).filter(tipo => tiposJustica[tipo]);
      if (tiposSelecionados.length > 0) {
        payload.justiceTypes = tiposSelecionados;
      }
      payload.tribunalSiglas = tribunalSiglas;

      // Adicionar reasoning se preenchido
      if (reasoning.trim()) {
        payload.reasoning = reasoning.trim();
      }

      const result = await apiService.post('/settings/classifications', payload);

      if (result) {
        setSuccess(true);
        setTermo('');
        setPrioridade('NORMAL');
        setArquivamentoAutomatico(false);
        setReasoning('');
        setTiposJustica({ comum: false, trabalho: false, federal: false });
        setTribunalSiglas([]);
        fetchClassificacoes(); // Recarregar lista
        
        // Fechar formulário e remover mensagem de sucesso após 2 segundos
        setTimeout(() => {
          setSuccess(false);
          setShowForm(false);
        }, 2000);
      }
    } catch (error) {
      // Capturar mensagem específica do servidor
      const errorMessage = error.response?.data?.error || 'Erro de conexão com o servidor';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Deletar classificação
  const handleDelete = async (id, nome) => {
    if (!confirm(`Tem certeza que deseja deletar a classificação "${nome}"?`)) {
      return;
    }

    try {
      const response = await apiService.delete(`/settings/classifications/${id}`);

      // Classification deleted successfully
      await fetchClassificacoes(); // Recarregar lista
      setError(null);
    } catch (error) {
      // Capturar mensagem específica do servidor
      const errorMessage = error.response?.data?.error || 'Erro ao deletar classificação';
      alert(errorMessage);
    }
  };

  // Funções para edição
  const handleEdit = (classificacao) => {
    setEditingId(classificacao.id);
    setEditForm({
      name: classificacao.name,
      priority: classificacao.priority || 'NORMAL',
      justiceTypes: {
        comum: classificacao.justiceTypes?.includes('comum') || false,
        trabalho: classificacao.justiceTypes?.includes('trabalho') || false,
        federal: classificacao.justiceTypes?.includes('federal') || false
      },
      tribunalSiglas: classificacao.tribunalSiglas || [],
      actions: {
        archive: classificacao.actions?.includes('archive') || false
      },
      reasoning: classificacao.reasoning || ''
    });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditForm({
      name: '',
      priority: 'NORMAL',
      justiceTypes: { comum: false, trabalho: false, federal: false },
      tribunalSiglas: [],
      actions: { archive: false },
      reasoning: ''
    });
  };

  const handleSaveEdit = async () => {
    if (!editForm.name.trim()) {
      alert('Nome é obrigatório');
      return;
    }

    try {
      const justiceTypesArray = Object.keys(editForm.justiceTypes)
        .filter(key => editForm.justiceTypes[key]);

      // Só inclui actions se a prioridade for "BAIXA"
      const actionsArray = editForm.priority === 'BAIXA'
        ? Object.keys(editForm.actions).filter(key => editForm.actions[key])
        : [];

      const updateData = {
        name: editForm.name.toUpperCase(),
        priority: editForm.priority,
        justiceTypes: justiceTypesArray.length > 0 ? justiceTypesArray : [],
        tribunalSiglas: editForm.tribunalSiglas || [],
        actions: actionsArray.length > 0 ? actionsArray : [],
        reasoning: editForm.reasoning || undefined
      };

      await apiService.put(`/settings/classifications/${editingId}`, updateData);

      await fetchClassificacoes();
      handleCancelEdit();
      setError(null);
    } catch (error) {
      // Capturar mensagem específica do servidor
      const errorMessage = error.response?.data?.error || 'Erro ao atualizar classificação';
      alert(errorMessage);
    }
  };

  // Função para obter cor da prioridade
  const getPriorityColor = (priority) => {
    const prioridadeObj = prioridades.find(p => p.value === priority);
    return prioridadeObj ? prioridadeObj.color : 'bg-gray-100 text-gray-700';
  };

  // Função para alternar ação de arquivamento
  const toggleArchiveAction = async (classificationId, currentActions) => {
    try {
      setError(null);

      // Verificar se já tem ação de arquivamento
      const hasArchiveAction = currentActions &&
        Array.isArray(currentActions) &&
        currentActions.includes('archive');

      // Toggle da ação
      let newActions = currentActions || [];
      if (hasArchiveAction) {
        // Remover ação de arquivamento
        newActions = newActions.filter(action => action !== 'archive');
      } else {
        // Adicionar ação de arquivamento
        newActions = [...newActions, 'archive'];
      }

      // Atualizar no backend
      await apiService.put(`/settings/classifications/${classificationId}`, {
        actions: newActions
      });

      // Atualizar estado local
      setClassificacoes(prev => prev.map(c =>
        c.id === classificationId
          ? { ...c, actions: newActions }
          : c
      ));

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);

    } catch (error) {
      // Capturar mensagem específica do servidor
      const errorMessage = error.response?.data?.error || 'Erro ao atualizar ação de arquivamento';
      setError(errorMessage);
    }
  };

  // Função para verificar se classificação tem ação de arquivamento
  const hasArchiveAction = (actions) => {
    return actions && Array.isArray(actions) && actions.includes('archive');
  };

  // Funções para polo passivo
  const fetchEmpresas = async () => {
    try {
      setLoadingEmpresas(true);
      const data = await apiService.get('/settings/companies');
      setEmpresas(data || []);
    } catch (error) {
      console.error('Erro ao carregar polos passivos:', error);
      setEmpresas([]);
    } finally {
      setLoadingEmpresas(false);
    }
  };

  const handleEmpresaSubmit = async (e) => {
    e.preventDefault();

    if (!empresaForm.nome.trim()) {
      setError('Nome do polo passivo é obrigatório');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const tiposJusticaSelecionados = Object.keys(empresaForm.tiposJustica).filter(
        tipo => empresaForm.tiposJustica[tipo]
      );

      const payload = {
        nome: empresaForm.nome,
        tier: empresaForm.tier,
        justiceTypes: tiposJusticaSelecionados.length > 0 ? tiposJusticaSelecionados : []
      };

      const result = await apiService.post('/settings/companies', payload);

      if (result) {
        await fetchEmpresas();

        setEmpresaForm({
          nome: '',
          tier: 'TIER I',
          tiposJustica: {
            comum: false,
            trabalho: false,
            federal: false
          }
        });
        setShowEmpresaForm(false);
        setSuccess(true);

        setTimeout(() => setSuccess(false), 3000);
      }
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Erro ao salvar polo passivo';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleEditEmpresa = (empresa) => {
    setEditingEmpresaId(empresa.id);
    setEditEmpresaForm({
      nome: empresa.nome,
      tier: empresa.tier,
      justiceTypes: {
        comum: empresa.justiceTypes?.includes('comum') || false,
        trabalho: empresa.justiceTypes?.includes('trabalho') || false,
        federal: empresa.justiceTypes?.includes('federal') || false
      }
    });
  };

  const handleCancelEditEmpresa = () => {
    setEditingEmpresaId(null);
    setEditEmpresaForm({
      nome: '',
      tier: 'TIER I',
      justiceTypes: { comum: false, trabalho: false, federal: false }
    });
  };

  const handleSaveEditEmpresa = async () => {
    if (!editEmpresaForm.nome.trim()) {
      alert('Nome do polo passivo é obrigatório');
      return;
    }

    try {
      const justiceTypesArray = Object.keys(editEmpresaForm.justiceTypes)
        .filter(key => editEmpresaForm.justiceTypes[key]);

      await apiService.put(`/settings/companies/${editingEmpresaId}`, {
        nome: editEmpresaForm.nome.toUpperCase(),
        tier: editEmpresaForm.tier,
        justiceTypes: justiceTypesArray.length > 0 ? justiceTypesArray : []
      });

      await fetchEmpresas();
      handleCancelEditEmpresa();
      setError(null);
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Erro ao atualizar polo passivo';
      alert(errorMessage);
    }
  };

  const handleDeleteEmpresa = async (id, nome) => {
    if (!confirm(`Tem certeza que deseja deletar o polo passivo "${nome}"?`)) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const result = await apiService.delete(`/settings/companies/${id}`);

      if (result !== false) {
        // Recarregar lista
        await fetchEmpresas();
        setSuccess(true);
        setTimeout(() => setSuccess(false), 3000);
      }
    } catch (error) {
      // Capturar mensagem específica do servidor
      const errorMessage = error.response?.data?.error || 'Erro ao deletar polo passivo';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };



  const handleExportCsv = async () => {
    try {
      setLoading(true);
      setError(null);

      const endpoint = activeTab === 'classificacoes'
        ? '/settings/classifications/export/csv'
        : '/settings/companies/export/csv';

      const fallbackFilename = activeTab === 'classificacoes'
        ? 'classificacoes.csv'
        : 'polos_passivos.csv';

      const { blob, filename } = await apiService.download(endpoint);
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');

      link.href = downloadUrl;
      link.download = filename || fallbackFilename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Erro ao exportar CSV';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Aviso de modo somente leitura */}
      {isReadOnly && (
        <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800">
          <Eye className="w-4 h-4 flex-shrink-0" />
          <span className="text-sm">
            Modo somente leitura: seu perfil (Tester) pode visualizar as configurações, mas não pode criar, editar ou excluir dados.
          </span>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <Settings className="w-8 h-8 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Configurações</h1>
            <p className="text-gray-600">
              {activeTab === 'classificacoes'
                ? 'Gerenciar classificações de publicações com tipos de justiça'
                : activeTab === 'classes-processuais'
                ? 'Parametrizar classes processuais aceitas e excluídas no fluxo DJE'
                : activeTab === 'tribunais'
                ? 'Parametrizar tribunais monitorados e valor mínimo por esfera de justiça'
                : activeTab === 'canais'
                ? 'Gerenciar canais WhatsApp do agente'
                : activeTab === 'playbook'
                ? 'Gerenciar playbook e identidade do agente'
                : 'Gerenciar polo passivo por tier'
              }
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {(activeTab === 'classificacoes' || activeTab === 'empresas') && (
            <button
              onClick={handleExportCsv}
              disabled={loading}
              className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <Download className="w-4 h-4" />
              Exportar CSV
            </button>
          )}

          {!isReadOnly && activeTab === 'classificacoes' && (
            <button
              onClick={() => setShowForm(!showForm)}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg transition-colors font-medium ${
                showForm
                  ? 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                  : 'bg-green-600 text-white hover:bg-green-700'
              }`}
            >
              {showForm ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
              {showForm ? 'Fechar' : 'Nova Classificação'}
            </button>
          )}
          {!isReadOnly && activeTab === 'empresas' && (
            <button
              onClick={() => {
                if (!showEmpresaForm) {
                  handleCancelEditEmpresa();
                }
                setShowEmpresaForm(!showEmpresaForm);
              }}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg transition-colors font-medium ${
                showEmpresaForm
                  ? 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                  : 'bg-green-600 text-white hover:bg-green-700'
              }`}
            >
              {showEmpresaForm ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
              {showEmpresaForm ? 'Fechar' : 'Novo Polo Passivo'}
            </button>
          )}
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex flex-wrap gap-x-8">
          <button
            onClick={() => handleTabChange('classificacoes')}
            className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center transition-colors ${
              activeTab === 'classificacoes'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <FileText className="mr-2 w-4 h-4" />
            Classificações
          </button>
          <button
            onClick={() => handleTabChange('classes-processuais')}
            className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center transition-colors ${
              activeTab === 'classes-processuais'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Scale className="mr-2 w-4 h-4" />
            Classes
          </button>
          <button
            onClick={() => handleTabChange('tribunais')}
            className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center transition-colors ${
              activeTab === 'tribunais'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Landmark className="mr-2 w-4 h-4" />
            Tribunais
          </button>
          <button
            onClick={() => handleTabChange('empresas')}
            className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center transition-colors ${
              activeTab === 'empresas'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Building2 className="mr-2 w-4 h-4" />
            Polo Passivo
          </button>
          <button
            onClick={() => handleTabChange('canais')}
            className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center transition-colors ${
              activeTab === 'canais'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <MessageSquare className="mr-2 w-4 h-4" />
            Canais WhatsApp
          </button>
          <button
            onClick={() => handleTabChange('playbook')}
            className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center transition-colors ${
              activeTab === 'playbook'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <BookOpen className="mr-2 w-4 h-4" />
            Playbook do Agente
          </button>
        </nav>
      </div>

      {/* Conteúdo das Abas */}
      {activeTab === 'classificacoes' && (
        <>
          {/* Formulário de Cadastro - Collapse */}
          {showForm && (
        <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <Plus className="w-5 h-5 text-green-600" />
            <h2 className="text-lg font-semibold text-gray-900">Nova Classificação</h2>
          </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Campo Termo */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Termo para Pesquisar *
              </label>
              <input
                type="text"
                value={termo}
                onChange={(e) => setTermo(e.target.value.toUpperCase())}
                placeholder="Ex: SENTENÇA TRANSITADA EM JULGADO"
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 bg-white"
                required
              />
            </div>

            {/* Campo Prioridade */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Prioridade
              </label>
              <select
                value={prioridade}
                onChange={(e) => setPrioridade(e.target.value)}
                className="w-full p-3 bg-white text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {prioridades.map((p) => (
                  <option key={p.value} value={p.value}>
                    {p.label}
                  </option>
                ))}
              </select>
            </div>

          </div>

          {/* Tipos de Justiça */}
          <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-700">
              Tipos de Justiça Aplicáveis
            </label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="flex items-center space-x-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <input
                  type="checkbox"
                  id="justica-comum"
                  checked={tiposJustica.comum}
                  onChange={(e) => handleTipoJusticaChange('comum', e.target.checked)}
                  className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 focus:ring-2"
                />
                <label htmlFor="justica-comum" className="text-sm text-blue-700 cursor-pointer font-medium">
                  Justiça Comum
                </label>
              </div>

              <div className="flex items-center space-x-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                <input
                  type="checkbox"
                  id="justica-trabalho"
                  checked={tiposJustica.trabalho}
                  onChange={(e) => handleTipoJusticaChange('trabalho', e.target.checked)}
                  className="w-4 h-4 text-green-600 rounded border-gray-300 focus:ring-green-500 focus:ring-2"
                />
                <label htmlFor="justica-trabalho" className="text-sm text-green-700 cursor-pointer font-medium">
                  Justiça do Trabalho
                </label>
              </div>

              <div className="flex items-center space-x-2 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                <input
                  type="checkbox"
                  id="justica-federal"
                  checked={tiposJustica.federal}
                  onChange={(e) => handleTipoJusticaChange('federal', e.target.checked)}
                  className="w-4 h-4 text-purple-600 rounded border-gray-300 focus:ring-purple-500 focus:ring-2"
                />
                <label htmlFor="justica-federal" className="text-sm text-purple-700 cursor-pointer font-medium">
                  Justiça Federal
                </label>
              </div>
            </div>
            <p className="text-xs text-gray-500">
              Selecione os tipos de justiça onde esta classificação deve ser aplicada.
              O mesmo termo pode ser cadastrado mais de uma vez com prioridades diferentes,
              desde que os tipos de justiça e tribunais não se sobreponham (ex.: termo para Trabalho com URGENTE e o mesmo termo para Federal com ALTA).
              Se nenhum for selecionado, aplica-se a todas as justiças e impede outro cadastro do mesmo termo.
            </p>
          </div>

          <TribunalPicker
            tribunals={tribunals}
            selectedSiglas={tribunalSiglas}
            justiceTypes={Object.keys(tiposJustica).filter((tipo) => tiposJustica[tipo])}
            onChange={setTribunalSiglas}
            disabled={isReadOnly}
          />

          {/* Campo Reasoning */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Fundamentação
            </label>
            <textarea
              value={reasoning}
              onChange={(e) => setReasoning(e.target.value)}
              placeholder="Descreva o motivo ou justificativa para esta classificação..."
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 bg-white resize-vertical min-h-[80px]"
              rows="3"
            />
            <p className="text-xs text-gray-500">
              Campo opcional para documentar o motivo e contexto desta classificação.
            </p>
          </div>

          {/* Checkbox de Arquivamento Automático - só aparece para prioridade BAIXA */}
          {prioridade === 'BAIXA' && (
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700">
                Ações Automáticas
              </label>
            <div className="flex items-center space-x-2 p-3 bg-red-50 border border-red-200 rounded-lg">
              <input
                type="checkbox"
                id="arquivamento-automatico"
                checked={arquivamentoAutomatico}
                onChange={(e) => setArquivamentoAutomatico(e.target.checked)}
                className="w-4 h-4 text-red-600 rounded border-gray-300 focus:ring-red-500 focus:ring-2"
              />
              <label htmlFor="arquivamento-automatico" className="flex items-center gap-2 text-sm text-red-700 cursor-pointer">
                <Archive className="w-4 h-4" />
                <span>Configurar para arquivamento automático</span>
              </label>
            </div>
            </div>
          )}

          {/* Mensagens */}
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">
              <AlertTriangle className="w-4 h-4" />
              {error}
            </div>
          )}

          {success && (
            <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700">
              <Check className="w-4 h-4" />
              Classificação cadastrada com sucesso!
            </div>
          )}

          {/* Botão Submit */}
          <button
            type="submit"
            disabled={loading}
            className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            <Plus className="w-4 h-4" />
            {loading ? 'Cadastrando...' : 'Cadastrar Classificação'}
          </button>
        </form>
        </div>
      )}

      {/* Colunas de Classificações por Prioridade (estilo Kanban) */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm">
        <div className="p-6 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Classificações por Prioridade</h2>
            <p className="text-sm text-gray-600">
              {classificacoes.length} {classificacoes.length === 1 ? 'classificação' : 'classificações'} organizada{classificacoes.length === 1 ? '' : 's'} em colunas
            </p>
          </div>
        </div>

        <div className="p-6">
          <>
            {loadingList ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <p className="mt-2 text-gray-600">Carregando classificações...</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {prioridades.map((prioridade) => {
                  const classificacoesDaPrioridade = classificacoes.filter(c => c.priority === prioridade.value);
                  const colorConfig = {
                    'URGENTE': { bg: 'bg-red-50', border: 'border-red-200', header: 'bg-red-100 text-red-800', count: 'bg-red-500 text-white', card: 'hover:border-red-300' },
                    'ALTA': { bg: 'bg-yellow-50', border: 'border-yellow-200', header: 'bg-yellow-100 text-yellow-800', count: 'bg-yellow-500 text-white', card: 'hover:border-yellow-300' },
                    'NORMAL': { bg: 'bg-blue-50', border: 'border-blue-200', header: 'bg-blue-100 text-blue-800', count: 'bg-blue-500 text-white', card: 'hover:border-blue-300' },
                    'BAIXA': { bg: 'bg-gray-50', border: 'border-gray-200', header: 'bg-gray-100 text-gray-800', count: 'bg-gray-500 text-white', card: 'hover:border-gray-300' }
                  };
                  const config = colorConfig[prioridade.value] || colorConfig['NORMAL'];

                  return (
                    <div key={prioridade.value} className={`${config.bg} ${config.border} border rounded-lg overflow-hidden`}>
                      {/* Header da Coluna */}
                      <div className={`${config.header} px-3 py-2 flex items-center justify-between`}>
                        <span className="font-semibold text-xs">
                          {prioridade.label}
                        </span>
                        <span className={`${config.count} text-xs px-1.5 py-0.5 rounded-full min-w-[18px] text-center font-medium`}>
                          {classificacoesDaPrioridade.length}
                        </span>
                      </div>

                      {/* Cards das Classificações */}
                      <div className="p-2 space-y-1.5 min-h-[200px]">
                        {classificacoesDaPrioridade.length === 0 ? (
                          <div className="text-center py-6 text-gray-400">
                            <FileText className="w-6 h-6 mx-auto mb-1 opacity-30" />
                            <p className="text-xs">Nenhuma classificação</p>
                          </div>
                        ) : (
                          classificacoesDaPrioridade.map((classificacao) => (
                            <div
                              key={classificacao.id || classificacao._id}
                              className={`bg-white p-2 rounded border border-gray-200 ${config.card} hover:shadow-sm transition-all group`}
                            >
                              {editingId === classificacao.id ? (
                                <div className="space-y-2">
                                  <input
                                    type="text"
                                    value={editForm.name}
                                    onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                                    className="w-full text-xs px-2 py-1 border border-gray-300 rounded focus:outline-none focus:border-blue-500 bg-white text-gray-900"
                                    placeholder="Nome da classificação"
                                  />

                                  {/* Prioridade - Edição */}
                                  <div className="space-y-1">
                                    <span className="text-xs text-gray-700 font-medium">Prioridade:</span>
                                    <select
                                      value={editForm.priority}
                                      onChange={(e) => setEditForm(prev => ({ ...prev, priority: e.target.value }))}
                                      className="w-full text-xs px-2 py-1 bg-white text-gray-900 border border-gray-300 rounded focus:outline-none focus:border-blue-500"
                                    >
                                      <option value="BAIXA">Baixa</option>
                                      <option value="NORMAL">Normal</option>
                                      <option value="ALTA">Alta</option>
                                      <option value="URGENTE">Urgente</option>
                                    </select>
                                  </div>

                                  {/* Tipos de Justiça - Edição */}
                                  <div className="space-y-1">
                                    <span className="text-xs text-gray-700 font-medium">Tipos de Justiça:</span>
                                    <div className="flex flex-wrap gap-1">
                                      {Object.entries({
                                        comum: 'Comum',
                                        trabalho: 'Trabalho',
                                        federal: 'Federal'
                                      }).map(([key, label]) => (
                                        <label key={key} className="flex items-center gap-1 text-xs text-gray-700 cursor-pointer">
                                          <input
                                            type="checkbox"
                                            checked={editForm.justiceTypes[key]}
                                            onChange={(e) => setEditForm(prev => {
                                              const nextJustice = {
                                                ...prev.justiceTypes,
                                                [key]: e.target.checked
                                              };
                                              const selectedTypes = Object.keys(nextJustice).filter((tipo) => nextJustice[tipo]);
                                              return {
                                                ...prev,
                                                justiceTypes: nextJustice,
                                                tribunalSiglas: (prev.tribunalSiglas || []).filter((sigla) => {
                                                  const tribunal = tribunals.find((item) => item.sigla === sigla);
                                                  return !selectedTypes.length || (tribunal && selectedTypes.includes(tribunal.justiceType));
                                                })
                                              };
                                            })}
                                            className="w-3 h-3 accent-blue-600"
                                          />
                                          <span className="text-gray-800">{label}</span>
                                        </label>
                                      ))}
                                    </div>
                                  </div>

                                  <TribunalPicker
                                    compact
                                    tribunals={tribunals}
                                    selectedSiglas={editForm.tribunalSiglas || []}
                                    justiceTypes={Object.keys(editForm.justiceTypes).filter((tipo) => editForm.justiceTypes[tipo])}
                                    onChange={(next) => setEditForm((prev) => ({ ...prev, tribunalSiglas: next }))}
                                  />

                                  {/* Ações - Edição (só aparece para prioridade BAIXA) */}
                                  {editForm.priority === 'BAIXA' && (
                                    <div className="space-y-1">
                                      <span className="text-xs text-gray-700 font-medium">Ações:</span>
                                      <div className="flex flex-wrap gap-1">
                                        <label className="flex items-center gap-1 text-xs text-gray-700 cursor-pointer">
                                          <input
                                            type="checkbox"
                                            checked={editForm.actions.archive}
                                            onChange={(e) => setEditForm(prev => ({
                                              ...prev,
                                              actions: {
                                                ...prev.actions,
                                                archive: e.target.checked
                                              }
                                            }))}
                                            className="w-3 h-3 accent-red-600"
                                          />
                                          <span className="text-red-800">Arquivar automaticamente</span>
                                        </label>
                                      </div>
                                    </div>
                                  )}

                                  {/* Reasoning - Edição */}
                                  <div className="space-y-1">
                                    <span className="text-xs text-gray-700 font-medium">Fundamentação:</span>
                                    <textarea
                                      value={editForm.reasoning}
                                      onChange={(e) => setEditForm(prev => ({ ...prev, reasoning: e.target.value }))}
                                      className="w-full text-xs px-2 py-1 border border-gray-300 rounded focus:outline-none focus:border-blue-500 bg-white text-gray-900 resize-vertical min-h-[60px]"
                                      placeholder="Justificativa para esta classificação"
                                      rows="2"
                                    />
                                  </div>

                                  <div className="flex gap-1">
                                    <button
                                      onClick={handleSaveEdit}
                                      className="p-0.5 text-green-600 hover:text-green-700 hover:bg-green-50 rounded"
                                      title="Salvar"
                                    >
                                      <Check className="w-3 h-3" />
                                    </button>
                                    <button
                                      onClick={handleCancelEdit}
                                      className="p-0.5 text-red-600 hover:text-red-700 hover:bg-red-50 rounded"
                                      title="Cancelar"
                                    >
                                      <X className="w-3 h-3" />
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <div className="space-y-2">
                                  <div className="flex items-start justify-between">
                                    <div className="flex-1 min-w-0">
                                      <h4
                                        className="font-medium text-gray-900 text-xs leading-tight truncate"
                                        title={classificacao.name}
                                      >
                                        {classificacao.name}
                                      </h4>

                                      {/* Tipos de Justiça */}
                                      {classificacao.justiceTypes && classificacao.justiceTypes.length > 0 && (
                                        <div className="flex flex-wrap gap-1 mt-2">
                                          {classificacao.justiceTypes.map((tipo) => {
                                            const tipoConfig = {
                                              'comum': { label: 'Comum', color: 'bg-blue-100 text-blue-600' },
                                              'trabalho': { label: 'Trabalho', color: 'bg-green-100 text-green-600' },
                                              'federal': { label: 'Federal', color: 'bg-purple-100 text-purple-600' }
                                            };
                                            const config = tipoConfig[tipo] || { label: tipo, color: 'bg-gray-100 text-gray-600' };

                                            return (
                                              <span
                                                key={tipo}
                                                className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${config.color}`}
                                              >
                                                {config.label}
                                              </span>
                                            );
                                          })}
                                        </div>
                                      )}
                                      <div className="flex flex-wrap gap-1 mt-1">
                                        {(classificacao.tribunalSiglas || []).length > 0 ? (
                                          (classificacao.tribunalSiglas || []).slice(0, 4).map((sigla) => (
                                            <span
                                              key={sigla}
                                              className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-700"
                                            >
                                              {sigla}
                                            </span>
                                          ))
                                        ) : (
                                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-500">
                                            Todos os tribunais
                                          </span>
                                        )}
                                        {(classificacao.tribunalSiglas || []).length > 4 && (
                                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-500">
                                            +{(classificacao.tribunalSiglas || []).length - 4}
                                          </span>
                                        )}
                                      </div>

                                    {/* Ícone visual para classificações configuradas para arquivamento */}
                                    {hasArchiveAction(classificacao.actions) && (
                                      <div className="flex items-center gap-1 mt-1">
                                        <div className="flex items-center gap-1 text-red-600">
                                          <Archive className="w-3 h-3" />
                                          <span className="text-xs font-medium">Arquivamento automático</span>
                                        </div>
                                      </div>
                                    )}

                                    {/* Reasoning - Exibição */}
                                    {classificacao.reasoning && (
                                      <div className="mt-2 p-2 bg-gray-50 border border-gray-200 rounded text-xs text-gray-600">
                                        <span className="font-medium text-gray-700">Fundamentação: </span>
                                        {classificacao.reasoning}
                                      </div>
                                    )}
                                    </div>
                                  </div>
                                  {!isReadOnly && (
                                    <div className="flex gap-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100 transition-all">
                                      <button
                                        onClick={() => handleEdit(classificacao)}
                                        className="p-0.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                                        title="Editar classificação"
                                      >
                                        <Edit className="w-3 h-3" />
                                      </button>
                                      <button
                                        onClick={() => handleDelete(classificacao.id, classificacao.name)}
                                        className="p-0.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                                        title="Deletar classificação"
                                      >
                                        <Trash2 className="w-3 h-3" />
                                      </button>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {!loadingList && classificacoes.length === 0 && (
              <div className="text-center py-12 text-gray-500">
                <Settings className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhuma classificação cadastrada</h3>
                <p className="text-gray-600">Cadastre sua primeira classificação usando o formulário acima.</p>
              </div>
            )}
          </>
        </div>
      </div>
        </>
      )}

      {/* Aba Empresas */}
      {activeTab === 'empresas' && (
        <>
          {/* Mensagens */}
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">
              <AlertTriangle className="w-4 h-4" />
              {error}
            </div>
          )}

          {success && (
            <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700">
              <Check className="w-4 h-4" />
              Operação realizada com sucesso!
            </div>
          )}

          {/* Formulário de Empresa */}
          {showEmpresaForm && (
            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-4">
                <Building2 className="w-5 h-5 text-green-600" />
                <h2 className="text-lg font-semibold text-gray-900">
                  Novo Polo Passivo
                </h2>
              </div>

              <form onSubmit={handleEmpresaSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nome do Polo Passivo *
                    </label>
                    <input
                      type="text"
                      value={empresaForm.nome}
                      onChange={(e) => setEmpresaForm(prev => ({ ...prev, nome: e.target.value.toUpperCase() }))}
                      placeholder="Ex: ADVOCACIA SILVA & ASSOCIADOS"
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 bg-white"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tier
                    </label>
                    <select
                      value={empresaForm.tier}
                      onChange={(e) => setEmpresaForm(prev => ({ ...prev, tier: e.target.value }))}
                      className="w-full p-3 bg-white text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      {tiers.map((tier) => (
                        <option key={tier.value} value={tier.value}>
                          {tier.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Tipos de Justiça */}
                <div className="space-y-3">
                  <label className="block text-sm font-medium text-gray-700">
                    Tipos de Justiça Aplicáveis
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div className="flex items-center space-x-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <input
                        type="checkbox"
                        id="empresa-justica-comum"
                        checked={empresaForm.tiposJustica.comum}
                        onChange={(e) => setEmpresaForm(prev => ({
                          ...prev,
                          tiposJustica: { ...prev.tiposJustica, comum: e.target.checked }
                        }))}
                        className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 focus:ring-2"
                      />
                      <label htmlFor="empresa-justica-comum" className="text-sm text-blue-700 cursor-pointer font-medium">
                        Justiça Comum
                      </label>
                    </div>

                    <div className="flex items-center space-x-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                      <input
                        type="checkbox"
                        id="empresa-justica-trabalho"
                        checked={empresaForm.tiposJustica.trabalho}
                        onChange={(e) => setEmpresaForm(prev => ({
                          ...prev,
                          tiposJustica: { ...prev.tiposJustica, trabalho: e.target.checked }
                        }))}
                        className="w-4 h-4 text-green-600 rounded border-gray-300 focus:ring-green-500 focus:ring-2"
                      />
                      <label htmlFor="empresa-justica-trabalho" className="text-sm text-green-700 cursor-pointer font-medium">
                        Justiça do Trabalho
                      </label>
                    </div>

                    <div className="flex items-center space-x-2 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                      <input
                        type="checkbox"
                        id="empresa-justica-federal"
                        checked={empresaForm.tiposJustica.federal}
                        onChange={(e) => setEmpresaForm(prev => ({
                          ...prev,
                          tiposJustica: { ...prev.tiposJustica, federal: e.target.checked }
                        }))}
                        className="w-4 h-4 text-purple-600 rounded border-gray-300 focus:ring-purple-500 focus:ring-2"
                      />
                      <label htmlFor="empresa-justica-federal" className="text-sm text-purple-700 cursor-pointer font-medium">
                        Justiça Federal
                      </label>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500">
                    Selecione os tipos de justiça onde este polo passivo atua.
                    O mesmo nome pode ser cadastrado mais de uma vez com tiers diferentes,
                    desde que os tipos de justiça não se sobreponham (ex.: polo para Trabalho com TIER I e o mesmo nome para Federal com TIER II).
                    Se nenhum for selecionado, aplica-se a todas as justiças e impede outro cadastro do mesmo nome.
                  </p>
                </div>

                <div className="flex gap-2">
                  <button
                    type="submit"
                    disabled={loading}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                  >
                    <Check className="w-4 h-4" />
                    {loading ? 'Salvando...' : 'Cadastrar Polo Passivo'}
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setShowEmpresaForm(false);
                      setEmpresaForm({
                        nome: '',
                        tier: 'TIER I',
                        tiposJustica: {
                          comum: false,
                          trabalho: false,
                          federal: false
                        }
                      });
                    }}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 font-medium"
                  >
                    <X className="w-4 h-4" />
                    Cancelar
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Colunas de Polo Passivo por Tier (estilo Kanban) */}
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Polo Passivo por Tier</h2>
                  <p className="text-sm text-gray-600">
                    {empresas.length} {empresas.length === 1 ? 'polo passivo' : 'polos passivos'} organizado{empresas.length === 1 ? '' : 's'} em colunas
                  </p>
                </div>
              </div>
            </div>

            <div className="p-6">
              {loadingEmpresas ? (
                <div className="text-center py-12">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <p className="mt-2 text-gray-600">Carregando polos passivos...</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {tiers.map((tier) => {
                    const empresasDoTier = empresas.filter(e => e.tier === tier.value);
                    const colorConfig = {
                      'TIER I': { bg: 'bg-green-50', border: 'border-green-200', header: 'bg-green-100 text-green-800', count: 'bg-green-500 text-white', card: 'hover:border-green-300' },
                      'TIER II': { bg: 'bg-yellow-50', border: 'border-yellow-200', header: 'bg-yellow-100 text-yellow-800', count: 'bg-yellow-500 text-white', card: 'hover:border-yellow-300' },
                      'TIER III': { bg: 'bg-red-50', border: 'border-red-200', header: 'bg-red-100 text-red-800', count: 'bg-red-500 text-white', card: 'hover:border-red-300' }
                    };
                    const config = colorConfig[tier.value] || colorConfig['TIER I'];

                    return (
                      <div key={tier.value} className={`${config.bg} ${config.border} border rounded-lg overflow-hidden`}>
                        {/* Header da Coluna */}
                        <div className={`${config.header} px-3 py-2 flex items-center justify-between`}>
                          <span className="font-semibold text-xs">
                            {tier.label}
                          </span>
                          <span className={`${config.count} text-xs px-1.5 py-0.5 rounded-full min-w-[18px] text-center font-medium`}>
                            {empresasDoTier.length}
                          </span>
                        </div>

                        {/* Cards das Empresas */}
                        <div className="p-2 space-y-1.5 min-h-[200px]">
                          {empresasDoTier.length === 0 ? (
                            <div className="text-center py-6 text-gray-400">
                              <Building2 className="w-6 h-6 mx-auto mb-1 opacity-30" />
                              <p className="text-xs">Nenhum polo passivo</p>
                            </div>
                          ) : (
                            empresasDoTier.map((empresa) => (
                              <div
                                key={empresa.id}
                                className={`bg-white p-2 rounded border border-gray-200 ${config.card} hover:shadow-sm transition-all group`}
                              >
                                {editingEmpresaId === empresa.id ? (
                                  <div className="space-y-2">
                                    <input
                                      type="text"
                                      value={editEmpresaForm.nome}
                                      onChange={(e) => setEditEmpresaForm(prev => ({ ...prev, nome: e.target.value.toUpperCase() }))}
                                      className="w-full text-xs px-2 py-1 border border-gray-300 rounded focus:outline-none focus:border-blue-500 bg-white text-gray-900"
                                      placeholder="Nome do polo passivo"
                                    />

                                    <div className="space-y-1">
                                      <span className="text-xs text-gray-700 font-medium">Tier:</span>
                                      <select
                                        value={editEmpresaForm.tier}
                                        onChange={(e) => setEditEmpresaForm(prev => ({ ...prev, tier: e.target.value }))}
                                        className="w-full text-xs px-2 py-1 bg-white text-gray-900 border border-gray-300 rounded focus:outline-none focus:border-blue-500"
                                      >
                                        {tiers.map((tier) => (
                                          <option key={tier.value} value={tier.value}>
                                            {tier.label}
                                          </option>
                                        ))}
                                      </select>
                                    </div>

                                    <div className="space-y-1">
                                      <span className="text-xs text-gray-700 font-medium">Tipos de Justiça:</span>
                                      <div className="flex flex-wrap gap-1">
                                        {Object.entries({
                                          comum: 'Comum',
                                          trabalho: 'Trabalho',
                                          federal: 'Federal'
                                        }).map(([key, label]) => (
                                          <label key={key} className="flex items-center gap-1 text-xs text-gray-700 cursor-pointer">
                                            <input
                                              type="checkbox"
                                              checked={editEmpresaForm.justiceTypes[key]}
                                              onChange={(e) => setEditEmpresaForm(prev => ({
                                                ...prev,
                                                justiceTypes: {
                                                  ...prev.justiceTypes,
                                                  [key]: e.target.checked
                                                }
                                              }))}
                                              className="w-3 h-3 accent-blue-600"
                                            />
                                            <span className="text-gray-800">{label}</span>
                                          </label>
                                        ))}
                                      </div>
                                    </div>

                                    <div className="flex gap-1">
                                      <button
                                        onClick={handleSaveEditEmpresa}
                                        className="p-0.5 text-green-600 hover:text-green-700 hover:bg-green-50 rounded"
                                        title="Salvar"
                                      >
                                        <Check className="w-3 h-3" />
                                      </button>
                                      <button
                                        onClick={handleCancelEditEmpresa}
                                        className="p-0.5 text-red-600 hover:text-red-700 hover:bg-red-50 rounded"
                                        title="Cancelar"
                                      >
                                        <X className="w-3 h-3" />
                                      </button>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="space-y-2">
                                    <div className="flex items-start justify-between">
                                      <div className="flex-1 min-w-0">
                                        <h4
                                          className="font-medium text-gray-900 text-xs leading-tight"
                                          title={empresa.nome}
                                        >
                                          {empresa.nome}
                                        </h4>

                                        {empresa.justiceTypes && empresa.justiceTypes.length > 0 && (
                                          <div className="flex flex-wrap gap-1 mt-2">
                                            {empresa.justiceTypes.map((tipo) => {
                                              const tipoConfig = {
                                                'comum': { label: 'Comum', color: 'bg-blue-100 text-blue-600' },
                                                'trabalho': { label: 'Trabalho', color: 'bg-green-100 text-green-600' },
                                                'federal': { label: 'Federal', color: 'bg-purple-100 text-purple-600' }
                                              };
                                              const badgeConfig = tipoConfig[tipo] || { label: tipo, color: 'bg-gray-100 text-gray-600' };

                                              return (
                                                <span
                                                  key={tipo}
                                                  className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${badgeConfig.color}`}
                                                >
                                                  {badgeConfig.label}
                                                </span>
                                              );
                                            })}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                    {!isReadOnly && (
                                      <div className="flex gap-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100 transition-all">
                                        <button
                                          onClick={() => handleEditEmpresa(empresa)}
                                          className="p-0.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                                          title="Editar polo passivo"
                                        >
                                          <Edit className="w-3 h-3" />
                                        </button>
                                        <button
                                          onClick={() => handleDeleteEmpresa(empresa.id, empresa.nome)}
                                          className="p-0.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                                          title="Deletar polo passivo"
                                        >
                                          <Trash2 className="w-3 h-3" />
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {!loadingEmpresas && empresas.length === 0 && (
                <div className="text-center py-12 text-gray-500">
                  <Building2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhum polo passivo cadastrado</h3>
                  <p className="text-gray-600">Cadastre o primeiro polo passivo usando o formulário acima.</p>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {activeTab === 'classes-processuais' && (
        <ClassesProcessuaisTab readOnly={isReadOnly} />
      )}

      {activeTab === 'tribunais' && (
        <TribunaisTab readOnly={isReadOnly} />
      )}

      {activeTab === 'canais' && <AgentChannels noPadding readOnly={isReadOnly} />}

      {activeTab === 'playbook' && <AgentPlaybook noPadding readOnly={isReadOnly} />}

    </div>
  );
}